export type PeriodType = "monthly" | "quarterly" | "yearly";

interface BaseDataPoint {
    month: [string, string];
    [key: string]: any;
}

export type AggregationStrategy = "sum" | "average" | "last" | "first";

export interface FieldAggregationConfig {
    [fieldName: string]: AggregationStrategy;
}

const MONTH_MAP: Record<string, number> = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
};

function getQuarter(month: number): number {
    return Math.ceil(month / 3);
}

function getQuarterLabel(quarter: number): string {
    return `Q${quarter}`;
}

function aggregateValues(
    values: (number | null)[],
    strategy: AggregationStrategy,
): number | null {
    const validValues = values.filter((v): v is number => v !== null);

    if (validValues.length === 0) return null;

    switch (strategy) {
        case "sum":
            return validValues.reduce((sum, val) => sum + val, 0);
        case "average":
            return (
                validValues.reduce((sum, val) => sum + val, 0) /
                validValues.length
            );
        case "last":
            return validValues[validValues.length - 1];
        case "first":
            return validValues[0];
        default:
            return validValues.reduce((sum, val) => sum + val, 0);
    }
}

export function aggregateDataByPeriod<T extends BaseDataPoint>(
    data: T[],
    period: PeriodType,
    aggregationConfig: FieldAggregationConfig,
): T[] {
    if (period === "monthly") {
        return data;
    }

    const grouped = data.reduce(
        (acc, item) => {
            const [year, monthName] = item.month;
            const monthNum = MONTH_MAP[monthName];

            let periodKey: string;
            let periodLabel: [string, string];

            if (period === "quarterly") {
                const quarter = getQuarter(monthNum);
                periodKey = `${year}-Q${quarter}`;
                periodLabel = [year, getQuarterLabel(quarter)];
            } else {
                periodKey = year;
                periodLabel = [year, ""];
            }

            if (!acc[periodKey]) {
                acc[periodKey] = {
                    items: [],
                    periodLabel,
                };
            }

            acc[periodKey].items.push(item);
            return acc;
        },
        {} as Record<string, { items: T[]; periodLabel: [string, string] }>,
    );

    return Object.values(grouped)
        .map(({ items, periodLabel }) => {
            const aggregated: any = {
                month: periodLabel,
            };

            Object.entries(aggregationConfig).forEach(
                ([fieldName, strategy]) => {
                    const values = items.map((item) => item[fieldName]);
                    aggregated[fieldName] = aggregateValues(values, strategy);
                },
            );

            const firstItem = items[0];
            Object.keys(firstItem).forEach((key) => {
                if (key !== "month" && !aggregationConfig[key]) {
                    aggregated[key] = items[items.length - 1][key];
                }
            });

            return aggregated as T;
        })
        .sort((a, b) => {
            const yearA = parseInt(a.month[0]);
            const yearB = parseInt(b.month[0]);

            if (yearA !== yearB) {
                return yearA - yearB;
            }

            if (period === "quarterly") {
                const quarterA = parseInt(a.month[1].replace("Q", ""));
                const quarterB = parseInt(b.month[1].replace("Q", ""));
                return quarterA - quarterB;
            }

            return 0;
        });
}

export const CHART_AGGREGATION_CONFIGS = {
    cashflowProgression: {
        totalRevenue: "sum",
        preMarketingEbitda: "sum",
        netIncome: "sum",
        grossMargin: "sum",
        changeInCash: "sum",
        ebitda: "sum",
    } as FieldAggregationConfig,

    financialData: {
        totalRevenue: "sum",
        projectedRevenue: "sum",
        grossMargin: "average",
        projectedGrossMargin: "average",
        salesAndMarketing: "sum",
        projectedSalesAndMarketing: "sum",
        totalExpenses: "sum",
        projectedTotalExpenses: "sum",
        preMarketingEbitda: "sum",
        projectedPreMarketingEbitda: "sum",
        ebitda: "sum",
        projectedEbitda: "sum",
    } as FieldAggregationConfig,

    debtComposition: {
        interestAndFees: "sum",
        debtServiceReconcilitationDiffP: "sum",
        interestExpense: "sum",
        debtBalanceEop: "last",
        totalDebt: "last",
        debtTotalReconciliationDiffP: "sum",
    } as FieldAggregationConfig,

    assetProgression: {
        totalAssets: "last",
        liquidAssets: "last",
        totalReceivables: "last",
        totalNoncurrentAssets: "last",
        noncurrentAssetsPropertyPlantAndEquipment: "last",
        cash: "last",
        totalInventory: "last",
        currentAssets: "last",
    } as FieldAggregationConfig,

    balanceOverTime: {
        balanceAvgInPeriod: "average",
        interestAndFees: "sum",
        debtBalanceBop: "first",
        capitalAtRiskEop: "last",
        debtBalanceEop: "last",
        amortization: "sum",
        disbursement: "sum",
        capitalAtRiskAvgInPeriod: "average",
        interestAndFeesCash: "sum",
        capitalAtRiskBop: "first",
        interestAndFeesAccrued: "sum",
    } as FieldAggregationConfig,

    liquidity: {
        liquidAssets: "last",
        totalExpenses: "sum",
        totalRevenue: "sum",
        totalCurrentLiabilities: "last",
        currentAssetsNetInventory: "last",
        currentRatio: "last",
        quickRatio: "last",
        cashRatio: "last",
        cash: "last",
        netIncome: "sum",
        ebitda: "sum",
        currentAssets: "last",
        runway: "last",
    } as FieldAggregationConfig,
};

export function aggregateChartData<T extends BaseDataPoint>(
    data: T[],
    period: PeriodType,
    chartType: keyof typeof CHART_AGGREGATION_CONFIGS,
): T[] {
    return aggregateDataByPeriod(
        data,
        period,
        CHART_AGGREGATION_CONFIGS[chartType],
    );
}
